import { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { aws } from '../lib/awsdb';
import { authWrite } from '../lib/authWrite';
import { usePolling } from '../lib/poll';
import Hub from './Hub';
import Catalogue from './Catalogue';
import IncomingTab from './IncomingTab';
import StockDashboard from './StockDashboard';
import SalesForecast from './SalesForecast';

const TAB_STORAGE_KEY = 'fon-stock-active-tab';
const VALID_TABS = new Set(['dashboard', 'onhand', 'sales', 'incoming', 'catalogue']);

const SERIF = '\'Tiempos\', Georgia, \'Times New Roman\', serif';
const MONO  = '\'JetBrains Mono\', ui-monospace, \'SF Mono\', Menlo, monospace';

export default function Forecaster({ session }) {
  // Persist the active tab so a page reload lands on whatever the user
  // was looking at. localStorage value is validated against VALID_TABS
  // before use — anything stale (e.g. an old `myday` value) falls back
  // to dashboard so the layout doesn't blank.
  const [tab, setTab] = useState(() => {
    try {
      const stored = localStorage.getItem(TAB_STORAGE_KEY);
      if (stored && VALID_TABS.has(stored)) return stored;
    } catch {}
    return 'dashboard';
  });
  useEffect(() => {
    try { localStorage.setItem(TAB_STORAGE_KEY, tab); } catch {}
  }, [tab]);

  // When a Dashboard "Needs attention" card is clicked, we land the user
  // on On Hand AND open the matching drawer. Hub picks this up via its
  // `openSkuId` prop and clears it back through `onCloseDrawer`.
  const [openSkuId, setOpenSkuId] = useState(null);

  /* ── Shared data loaded once ── */
  const [skus, setSkus] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [weeklyData, setWeeklyData] = useState([]);
  const [incomingData, setIncomingData] = useState([]);

  const loadSkus = useCallback(async () => { const { data } = await aws.from('forecaster_skus').select('*').eq('is_active', true).order('sku_name'); setSkus(data || []); }, []);
  const loadCatalog = useCallback(async () => { const { data } = await aws.from('forecaster_catalog').select('*').eq('is_active', true).neq('status', 'retired').order('sku_name'); setCatalog(data || []); }, []);
  const loadWeekly = useCallback(async () => { const { data } = await aws.from('forecaster_weekly_sales').select('*').order('week_start', { ascending: false }); setWeeklyData(data || []); }, []);
  const loadIncoming = useCallback(async () => { const { data } = await aws.from('forecaster_incoming_stock').select('*').order('expected_date'); setIncomingData(data || []); }, []);

  /* ── Initial load + auto-sync catalog ── DO NOT TOUCH (load-bearing) ── */
  useEffect(() => {
    async function init() {
      const [skuRes, catRes] = await Promise.all([
        aws.from('forecaster_skus').select('*').eq('is_active', true).order('sku_name'),
        aws.from('forecaster_catalog').select('*').eq('is_active', true).neq('status', 'retired').order('sku_name'),
      ]);
      const skuData = skuRes.data || [];
      const catData = catRes.data || [];
      const existing = new Set(skuData.map(s => s.catalog_id).filter(Boolean));
      const missing = catData.filter(c => c.status === 'active' && !existing.has(c.id));
      if (missing.length > 0) {
        // Direct inserts on the now-writable AWS PostgREST. forecaster_skus
        // has a PARTIAL unique index on catalog_id (WHERE catalog_id IS NOT NULL),
        // so ON CONFLICT won't match — we tolerate 23505 (unique_violation)
        // instead and surface anything else.
        for (const c of missing) {
          try {
            await authWrite(session, 'forecaster_skus', 'insert', {
              sku_name: c.sku_name, catalog_id: c.id,
              lead_time_days: c.lead_time_days, batch_size: c.batch_size,
              target_cover_weeks: c.target_cover_weeks, updated_by: 'auto-sync',
            });
          } catch (err) {
            // 23505 = unique_violation; safe to swallow (already synced).
            if (!/duplicate key|23505|already exists/i.test(err?.message || '')) {
              console.warn('[forecaster auto-sync] insert failed for', c.sku_name, err.message);
            }
          }
        }
        const { data: fresh } = await aws.from('forecaster_skus').select('*').eq('is_active', true).order('sku_name');
        setSkus(fresh || []);
      } else {
        setSkus(skuData);
      }
      setCatalog(catData);
      loadWeekly(); loadIncoming();
    }
    init();
  }, [loadWeekly, loadIncoming]);

  usePolling([loadSkus, loadCatalog, loadWeekly, loadIncoming]);

  const shared = { skus, catalog, weeklyData, incomingData, loadSkus, loadCatalog, loadWeekly, loadIncoming };

  // Tab counts — pure derivations, no extra fetches.
  const counts = {
    onhand: skus.length,
    incoming: incomingData.length,
    catalogue: catalog.length,
  };

  return (
    <div className="app-screen">
      <StockHeader tab={tab} setTab={setTab} counts={counts} />
      <div className="main-content">
        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
          >
            {tab === 'dashboard' && (
              <StockDashboard
                {...shared}
                onNavigate={setTab}
                onOpenSku={(id) => { setOpenSkuId(id); setTab('onhand'); }}
              />
            )}
            {tab === 'onhand' && (
              <Hub
                session={session}
                {...shared}
                openSkuId={openSkuId}
                onCloseDrawer={() => setOpenSkuId(null)}
              />
            )}
            {tab === 'sales' && <SalesForecast session={session} {...shared} />}
            {tab === 'incoming' && <IncomingTab session={session} {...shared} />}
            {tab === 'catalogue' && <Catalogue session={session} {...shared} />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

// ── Header — eyebrow + serif title + tab strip with counts ──────────────
// Inline styles to avoid touching app.css (the legacy `fc-subtab` purple-
// accent rule still applies to the AdminPanel sub-tabs and shouldn't be
// changed here).
function StockHeader({ tab, setTab, counts }) {
  const tabs = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'onhand',    label: 'On hand',   count: counts.onhand },
    { id: 'sales',     label: 'Sales' },
    { id: 'incoming',  label: 'Incoming',  count: counts.incoming },
    { id: 'catalogue', label: 'Catalogue', count: counts.catalogue },
  ];
  return (
    <div style={{ padding: '28px 40px 0', display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{
          fontFamily: MONO, fontSize: 10, letterSpacing: '0.14em',
          textTransform: 'uppercase', color: '#6E7278',
        }}>Inventory</div>
        <h1 style={{
          fontFamily: SERIF, fontSize: 30, letterSpacing: '-0.015em',
          color: '#0F1419', margin: 0, fontWeight: 400,
        }}>Stock</h1>
      </div>
      <div style={{
        display: 'flex', gap: 2, borderBottom: '1px solid #E8E6E0', marginTop: 8,
      }}>
        {tabs.map(t => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                position: 'relative',
                padding: '12px 18px 14px', marginBottom: -1,
                background: 'transparent', border: 'none', cursor: 'pointer',
                fontSize: 13, letterSpacing: '-0.005em',
                color: active ? '#0F1419' : '#6E7278',
                fontWeight: active ? 500 : 400,
                borderBottom: active ? '2px solid #0F8E6F' : '2px solid transparent',
              }}
            >
              {t.label}
              {typeof t.count === 'number' && (
                <span style={{
                  marginLeft: 7, padding: '1px 7px', borderRadius: 999,
                  fontFamily: MONO, fontSize: 10,
                  background: active ? '#0F8E6F' : '#E8E6E0',
                  color: active ? '#fff' : '#6E7278',
                }}>{t.count}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
